/**
 * 移动工作台子路由（/m 与桌面内嵌 /desktop-mobile 共用，组件相同、name 前缀不同）
 */
import { useUserStore } from '@/store/user'
import { getFirstAllowedMobilePath } from '@/utils/mobileAccess'
import { joinMobilePath } from '@/utils/mobileWorkbenchBase'
import MobileInventoryDetail from '@/views/mobile/MobileInventoryDetail.vue'
import MobileWarehouseBrowse from '@/views/mobile/MobileWarehouseBrowse.vue'
import MobileDispatchList from '@/views/mobile/MobileDispatchList.vue'
import MobileDispatchCreate from '@/views/mobile/MobileDispatchCreate.vue'
import MobileDispatchPackaging from '@/views/mobile/MobileDispatchPackaging.vue'
import MobileDispatchDetail from '@/views/mobile/MobileDispatchDetail.vue'

const CASH_REIMBURSEMENT_ROUTE_PERMS = [
  'mobile.cash_reimbursement',
  'finance.cash_reimbursement.submit',
  'finance.cash_reimbursement.view',
  'finance.cash_reimbursement.review',
  'finance.view'
]

const TEST_REPORT_ROUTE_PERMS = [
  'mobile.test_reports',
  'quality.test_reports.view',
  'quality.test_reports.edit',
  'quality.test_reports.delete'
]

/**
 * @param {{ basePath: string, namePrefix?: string }} opts
 */
export function getMobileWorkbenchChildren({ basePath, namePrefix = '' }) {
  const n = (name) => (namePrefix ? `${namePrefix}${name}` : name)
  const base = basePath.replace(/\/$/, '')

  return [
    {
      path: '',
      redirect: () => {
        const u = useUserStore().user
        if (u) return getFirstAllowedMobilePath(u, base)
        return joinMobilePath(base, 'inventory')
      }
    },
    {
      path: 'home',
      name: n('MobileWorkbenchHome'),
      component: () => import('@/views/mobile/MobileWorkbenchHome.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: 'inventory',
      name: n('MobileInventoryList'),
      component: () => import('@/views/mobile/MobileInventoryList.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: 'inventory/:id',
      name: n('MobileInventoryDetail'),
      component: MobileInventoryDetail,
      meta: { requiresAuth: true }
    },
    {
      path: 'warehouse',
      name: n('MobileWarehouseBrowse'),
      component: MobileWarehouseBrowse,
      meta: { requiresAuth: true }
    },
    {
      path: 'inventory-approvals',
      name: n('MobileInventoryOperationApprovals'),
      component: () => import('@/views/mobile/MobileInventoryOperationApprovals.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: [
          'mobile.inventory_operation_approval',
          'inventory.operation_approval.review',
          'inventory.operation_approval.initial_review'
        ]
      }
    },
    {
      path: 'inventory-approval/:requestId',
      name: n('MobileInventoryOperationApprovalDetail'),
      component: () => import('@/views/mobile/MobileInventoryOperationApprovalDetail.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: [
          'mobile.inventory_operation_approval',
          'inventory.operation_approval.review',
          'inventory.operation_approval.initial_review'
        ]
      }
    },
    {
      path: 'delivery-sync-price/:requestId',
      name: n('MobileDeliveryUnitPriceSyncDetail'),
      component: () => import('@/views/mobile/MobileDeliveryUnitPriceSyncDetail.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: [
          'orders.delivery.sync_price.review',
          'orders.delivery.sync_price.request',
          'mobile.tasks',
          'tasks.view'
        ]
      }
    },
    {
      path: 'inventory-location/:id',
      name: n('MobileInventoryLocationConfirm'),
      component: () => import('@/views/mobile/MobileInventoryLocationConfirm.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: [
          'inventory.records.edit',
          'inventory.records.view',
          'mobile.inventory',
          'mobile.tasks',
          'tasks.view'
        ]
      }
    },
    {
      path: 'inventory-transaction/:id',
      name: n('MobileInventoryTransactionDetail'),
      component: () => import('@/views/mobile/MobileInventoryTransactionDetail.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: ['mobile.tasks', 'tasks.view', 'inventory.operations.view']
      }
    },
    {
      path: 'orders',
      name: n('MobileOrdersList'),
      component: () => import('@/views/mobile/MobileOrdersList.vue'),
      meta: { requiresAuth: true, permission: 'orders.sales.view' }
    },
    {
      path: 'orders/overview',
      name: n('MobileOrderOverview'),
      component: () => import('@/views/mobile/MobileOrderOverview.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: ['mobile.order_overview', 'mobile.orders', 'orders.sales.view']
      }
    },
    {
      path: 'orders/overview/customer/:customerId',
      name: n('MobileOrderOverviewMaterials'),
      component: () => import('@/views/mobile/MobileOrderOverview.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: ['mobile.order_overview', 'mobile.orders', 'orders.sales.view']
      }
    },
    {
      path: 'orders/overview/customer/:customerId/material/:materialKey',
      name: n('MobileOrderOverviewColors'),
      component: () => import('@/views/mobile/MobileOrderOverview.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: ['mobile.order_overview', 'mobile.orders', 'orders.sales.view']
      }
    },
    {
      path: 'orders/shortage',
      name: n('MobileMaterialShortage'),
      component: () => import('@/views/mobile/MobileMaterialShortage.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: [
          'mobile.material_shortage',
          'mobile.orders',
          'orders.material_matching.view'
        ]
      }
    },
    {
      path: 'orders/shortage/material/:materialKey',
      name: n('MobileMaterialShortageColors'),
      component: () => import('@/views/mobile/MobileMaterialShortage.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: [
          'mobile.material_shortage',
          'mobile.orders',
          'orders.material_matching.view'
        ]
      }
    },
    {
      path: 'orders/shortage/material/:materialKey/color/:colorKey',
      name: n('MobileMaterialShortageCustomers'),
      component: () => import('@/views/mobile/MobileMaterialShortage.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: [
          'mobile.material_shortage',
          'mobile.orders',
          'orders.material_matching.view'
        ]
      }
    },
    {
      path: 'orders/:id',
      name: n('MobileOrderDetail'),
      component: () => import('@/views/mobile/MobileOrderDetail.vue'),
      meta: { requiresAuth: true, permission: 'orders.sales.view' }
    },
    {
      path: 'matching',
      name: n('MobileMaterialMatching'),
      component: () => import('@/views/mobile/MobileMaterialMatching.vue'),
      meta: { requiresAuth: true, permission: 'orders.material_matching.view' }
    },
    {
      path: 'matching/:orderItemId',
      name: n('MobileMaterialMatchingDetail'),
      component: () => import('@/views/mobile/MobileMaterialMatchingDetail.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: ['orders.material_matching.view', 'purchase.processing_follow.view']
      }
    },
    {
      path: 'dispatch',
      name: n('MobileDispatchList'),
      component: MobileDispatchList,
      meta: {
        requiresAuth: true,
        permissionAny: [
          'orders.dispatch.view',
          'orders.dispatch.edit',
          'orders.dispatch.review',
          'operations.execute'
        ]
      }
    },
    {
      path: 'dispatch/create',
      name: n('MobileDispatchCreate'),
      component: MobileDispatchCreate,
      meta: { requiresAuth: true, permission: 'orders.dispatch.edit' }
    },
    {
      path: 'dispatch/:id/packaging',
      name: n('MobileDispatchPackaging'),
      component: MobileDispatchPackaging,
      meta: {
        requiresAuth: true,
        title: '包装称重'
      }
    },
    {
      path: 'dispatch/:id',
      name: n('MobileDispatchDetail'),
      component: MobileDispatchDetail,
      meta: {
        requiresAuth: true,
        permissionAny: [
          'orders.dispatch.view',
          'orders.dispatch.edit',
          'orders.dispatch.review',
          'operations.execute'
        ]
      }
    },
    {
      path: 'processing-follow-up',
      name: n('MobileProcessingFollowUp'),
      component: () => import('@/views/mobile/MobileProcessingFollowUp.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: [
          'purchase.processing_follow.view',
          'purchase.processing.field',
          'orders.material_matching.view',
          'mobile.processing_follow'
        ]
      }
    },
    {
      path: 'color-formulation/:id',
      name: n('MobileColorFormulationDetail'),
      component: () => import('@/views/mobile/MobileColorFormulationDetail.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: [
          'purchase.color_formulation.field',
          'purchase.color_formulation.view',
          'purchase.color_formulation.audit',
          'orders.material_matching.view',
          'mobile.color_formulation',
          'mobile.tasks'
        ]
      }
    },
    {
      path: 'market-procurement/:id',
      name: n('MobileMarketProcurementDetail'),
      component: () => import('@/views/mobile/MobileMarketProcurementDetail.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: [
          'purchase.market_procurement.field',
          'purchase.market_procurement.view',
          'purchase.market_procurement.audit',
          'orders.material_matching.view',
          'mobile.market_procurement',
          'mobile.tasks'
        ]
      }
    },
    {
      path: 'processing',
      name: n('MobileProcessingOrders'),
      component: () => import('@/views/mobile/MobileProcessingOrders.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: [
          'purchase.processing.field',
          'purchase.processing_follow.view',
          'orders.material_matching.view',
          'mobile.processing_orders'
        ]
      }
    },
    {
      path: 'processing/:id',
      name: n('MobileProcessingOrderDetail'),
      component: () => import('@/views/mobile/MobileProcessingOrderDetail.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: [
          'purchase.processing.field',
          'purchase.processing_follow.view',
          'orders.material_matching.view',
          'mobile.processing_orders',
          'mobile.processing_follow',
          'mobile.tasks'
        ]
      }
    },
    {
      path: 'task-center',
      name: n('MobileTaskCenter'),
      component: () => import('@/views/mobile/MobileTaskCenter.vue'),
      meta: { requiresAuth: true, permission: 'tasks.view' }
    },
    {
      path: 'task-center/user/:userId',
      name: n('MobileTaskUserTypes'),
      component: () => import('@/views/mobile/MobileTaskUserTypes.vue'),
      meta: { requiresAuth: true, permission: 'tasks.view' }
    },
    {
      path: 'task-center/user/:userId/:categoryKey/suppliers',
      name: n('MobileTaskProcessingSuppliers'),
      component: () => import('@/views/mobile/MobileTaskProcessingSuppliers.vue'),
      meta: { requiresAuth: true, permission: 'tasks.view' }
    },
    {
      path: 'task-center/user/:userId/:categoryKey/customers',
      name: n('MobileTaskPackagingCustomers'),
      component: () => import('@/views/mobile/MobileTaskPackagingCustomers.vue'),
      meta: { requiresAuth: true, permission: 'tasks.view' }
    },
    {
      path: 'task-center/user/:userId/delivery/buckets',
      name: n('MobileTaskDeliveryBuckets'),
      component: () => import('@/views/mobile/MobileTaskDeliveryBuckets.vue'),
      meta: { requiresAuth: true, permission: 'tasks.view' }
    },
    {
      path: 'task-center/user/:userId/delivery/bucket/:bucketDays/customers',
      name: n('MobileTaskDeliveryCustomers'),
      component: () => import('@/views/mobile/MobileTaskDeliveryCustomers.vue'),
      meta: { requiresAuth: true, permission: 'tasks.view' }
    },
    {
      path: 'task-center/user/:userId/delivery/bucket/:bucketDays/customer/:customerKey/materials',
      name: n('MobileTaskDeliveryMaterials'),
      component: () => import('@/views/mobile/MobileTaskDeliveryMaterials.vue'),
      meta: { requiresAuth: true, permission: 'tasks.view' }
    },
    {
      path: 'task-center/user/:userId/:categoryKey/materials',
      name: n('MobileTaskMaterialCategories'),
      component: () => import('@/views/mobile/MobileTaskMaterialCategories.vue'),
      meta: { requiresAuth: true, permission: 'tasks.view' }
    },
    {
      path: 'task-center/user/:userId/:categoryKey',
      name: n('MobileTaskCategoryList'),
      component: () => import('@/views/mobile/MobileTaskCategoryList.vue'),
      meta: { requiresAuth: true, permission: 'tasks.view' }
    },
    {
      path: 'task-center/user/:userId/:categoryKey/packaging-bulk',
      name: n('MobileTaskPackagingBulk'),
      component: () => import('@/views/mobile/MobileTaskPackagingBulk.vue'),
      meta: { requiresAuth: true, permission: 'tasks.view' }
    },
    {
      path: 'manual-task/:id',
      name: n('MobileManualTaskDetail'),
      component: () => import('@/views/mobile/MobileManualTaskDetail.vue'),
      meta: { requiresAuth: true, permission: 'tasks.view' }
    },
    {
      path: 'finance-brief',
      name: n('MobileFinanceBrief'),
      component: () => import('@/views/mobile/MobileFinanceBrief.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: [
          'finance.view',
          'finance.documents.view',
          'finance.cash_reimbursement.view',
          'finance.cash_reimbursement.review'
        ]
      }
    },
    {
      path: 'finance-vouchers',
      name: n('MobileFinanceVoucherList'),
      component: () => import('@/views/mobile/MobileFinanceVoucherList.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: ['finance.view', 'finance.documents.view']
      }
    },
    {
      path: 'finance-vouchers/:id',
      name: n('MobileFinanceVoucherDetail'),
      component: () => import('@/views/mobile/MobileFinanceVoucherDetail.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: ['finance.view', 'finance.documents.view']
      }
    },
    {
      path: 'finance-reconciliation',
      name: n('MobileReconciliationDashboard'),
      component: () => import('@/views/mobile/MobileReconciliationDashboard.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: ['finance.view', 'billing.statements.view']
      }
    },
    {
      path: 'my-receivables',
      name: n('MobileMyReceivables'),
      component: () => import('@/views/mobile/MobileReconciliationDashboard.vue'),
      meta: {
        requiresAuth: true,
        receivableOnly: true,
        permissionAny: ['finance.receivables.view', 'finance.view', 'billing.statements.view']
      }
    },
    {
      path: 'finance-reconciliation/:direction/party/:partyId',
      name: n('MobileReconciliationPartyDetail'),
      component: () => import('@/views/mobile/MobileReconciliationPartyDetail.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: ['finance.view', 'billing.statements.view', 'finance.receivables.view']
      }
    },
    {
      path: 'my-receivables/party/:partyId',
      name: n('MobileMyReceivablesPartyDetail'),
      component: () => import('@/views/mobile/MobileReconciliationPartyDetail.vue'),
      meta: {
        requiresAuth: true,
        receivableOnly: true,
        permissionAny: ['finance.receivables.view', 'finance.view', 'billing.statements.view']
      }
    },
    {
      path: 'cash-reimbursement',
      name: n('MobileCashReimbursementList'),
      component: () => import('@/views/mobile/MobileCashReimbursementList.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: CASH_REIMBURSEMENT_ROUTE_PERMS
      }
    },
    {
      path: 'cash-reimbursement/new',
      name: n('MobileCashReimbursementCreate'),
      component: () => import('@/views/mobile/MobileCashReimbursementForm.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: CASH_REIMBURSEMENT_ROUTE_PERMS
      }
    },
    {
      path: 'cash-reimbursement/:id/edit',
      name: n('MobileCashReimbursementForm'),
      component: () => import('@/views/mobile/MobileCashReimbursementForm.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: CASH_REIMBURSEMENT_ROUTE_PERMS
      }
    },
    {
      path: 'cash-reimbursement/:id',
      name: n('MobileCashReimbursementDetail'),
      component: () => import('@/views/mobile/MobileCashReimbursementDetail.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: CASH_REIMBURSEMENT_ROUTE_PERMS
      }
    },
    {
      path: 'test-reports',
      name: n('MobileTestReportList'),
      component: () => import('@/views/mobile/MobileTestReportList.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: TEST_REPORT_ROUTE_PERMS
      }
    },
    {
      path: 'test-reports/new',
      name: n('MobileTestReportCreate'),
      component: () => import('@/views/mobile/MobileTestReportForm.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: ['mobile.test_reports', 'quality.test_reports.edit']
      }
    },
    {
      path: 'test-reports/:id',
      name: n('MobileTestReportDetail'),
      component: () => import('@/views/mobile/MobileTestReportDetail.vue'),
      meta: {
        requiresAuth: true,
        permissionAny: TEST_REPORT_ROUTE_PERMS
      }
    }
  ]
}
